import { VIRTUAL_TEMPLATES } from './virtual_template_config';
import { VIRTUAL_NUMBER_MAX_VALUE, VIRTUAL_NUMBER_MIN_VALUE } from '../virtual_components_constants';
//Types
import type { VCInterfaceStatus } from 'src/types/VirtualComponents';
import type { VirtualDeviceTemplate } from './virtual_template_types';
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

/** Translates a message key and interpolates the supplied string values. */
type Format = (key: string, options?: { default?: string; values?: Record<string, string> }) => string;

/** User-facing validation error displayed by the virtual component UI. */
type ValidationError = {
	heading: string;
	message: string;
	type: 'danger' | 'warning';
	component?: string;
	settingsTarget?: 'component' | 'template';
};

/** Describes a localizable problem found in a virtual component status. */
type ValidationIssue = { key: string; message: string; values?: Record<string, string> };

/**
 * Checks whether a virtual template contains all roles required by its template configuration.
 *
 * Optional roles and the template's `type` field are not required to reference a component.
 *
 * @param virtualTemplate - The virtual template to validate.
 * @returns `true` when every required configured role has a component assigned.
 */
export const validateVirtualTemplate = (virtualTemplate: VirtualDeviceTemplate): boolean => {
	const virtualTemplateConfig = VIRTUAL_TEMPLATES[virtualTemplate?.type] || {};
	return Object.keys(virtualTemplateConfig).every(
		(role) =>
			role === 'type' ||
			virtualTemplateConfig[role]?.optional === true ||
			(virtualTemplate?.[role] != null && virtualTemplateConfig[role]?.type),
	);
};

/**
 * Validates the components assigned to a virtual template against their current statuses.
 *
 * Each assigned component must have a matching status, the component type expected for its
 * template role, and a valid type-specific configuration and value.
 *
 * @param virtualTemplate - The template whose assigned components should be validated.
 * @param statuses - Current virtual component statuses used to resolve template assignments.
 * @returns Localized validation errors, or `null` when all assigned components are valid.
 */
export const validateVirtualTemplateComponents = (
	virtualTemplate: VirtualDeviceTemplate,
	statuses: VCInterfaceStatus[] = [],
): ValidationError[] | null => {
	const $format = get(format);
	const errors: ValidationError[] = [];
	const templateConfig = VIRTUAL_TEMPLATES[virtualTemplate.type];
	for (const role of Object.keys(templateConfig || {})) {
		if (role === 'type' || templateConfig[role]?.optional === true) continue;
		if (virtualTemplate[role] == null) {
			errors.push(createMissingComponentError(role, $format));
		}
	}
	for (const [role, component] of Object.entries(virtualTemplate)) {
		if (role === 'type') continue;
		const expectedType = templateConfig?.[role]?.type;
		const status = statuses.find((item) => item?.component === component);
		if (!status) {
			errors.push(createMissingComponentError(component, $format));
			continue;
		}
		if (!expectedType || status.vcType !== expectedType) {
			errors.push(
				createInvalidComponentError(
					{
						key: 'vc_template_component_type_mismatch',
						message: 'Expected {expectedType} component, received {actualType}',
						values: {
							expectedType: expectedType || role,
							actualType: status.vcType || 'unknown',
						},
					},
					status,
					$format,
				),
			);
			continue;
		}
		const issue = getComponentValidationIssue(status);
		if (issue) errors.push(createInvalidComponentError(issue, status, $format));
	}
	return errors.length ? errors : null;
};

/**
 * Validates virtual component statuses independently of a virtual template.
 *
 * Enum and number components receive type-specific validation. Other non-BTHome component types
 * are only checked for a configuration object. BTHome components do not require one.
 *
 * @param statuses - Virtual component statuses to validate.
 * @returns Localized validation errors, or `null` when every component is valid.
 */
export const validateVirtualComponents = (
	statuses: VCInterfaceStatus[] = [],
): ValidationError[] | null => {
	const $format = get(format);
	const errors = statuses.flatMap((status) => {
		const issue = getComponentValidationIssue(status);
		return issue ? [createInvalidComponentError(issue, status, $format, false)] : [];
	});
	return errors.length ? errors : null;
};

/**
 * Creates a localized error for a component referenced by a template but absent from statuses.
 *
 * @param component - Identifier of the missing component.
 * @param $format - Translation formatter used for the error text.
 * @returns A danger-level validation error.
 */
const createMissingComponentError = (component: string, $format: Format): ValidationError => ({
	type: 'danger',
	settingsTarget: 'template',
	heading: $format('vc_template_missing_component', {
		default: 'Template component is missing.',
	}),
	message: `${$format('device_component_missing', {
		default: 'Device component is missing',
	})}: ${component}`,
});

/**
 * Creates a localized warning for an invalid virtual component.
 *
 * @param issue - Validation issue containing the translation key and interpolation values.
 * @param status - Status of the invalid component.
 * @param $format - Translation formatter used for the warning text.
 * @param isTemplateComponent - Whether to use the template-specific warning heading.
 * @returns A warning-level validation error containing the component name and identifier.
 */
const createInvalidComponentError = (
	issue: ValidationIssue,
	status: VCInterfaceStatus,
	$format: Format,
	isTemplateComponent = true,
): ValidationError => {
	const componentName = status.vcName ? `${status.vcName} (${status.component})` : status.component;
	return {
		type: 'warning',
		component: status.component,
		settingsTarget: 'component',
		heading: $format(
			isTemplateComponent
				? 'vc_template_invalid_component_heading'
				: 'virtual_component_invalid_heading',
			{
				default: isTemplateComponent
					? 'Template component is invalid.'
					: 'Virtual component is invalid.',
			},
		),
		message: `${$format(issue.key, {
			default: issue.message,
			values: issue.values,
		})}: ${componentName}`,
	};
};

/**
 * Returns the first validation issue found in a virtual component status.
 *
 * Missing configuration takes precedence over type-specific enum or number validation, except
 * for BTHome components, which do not require configuration.
 * Component types without additional validation are considered valid when configuration exists.
 *
 * @param status - Virtual component status to inspect.
 * @returns The first validation issue, or `null` when the status is valid.
 */
const getComponentValidationIssue = (status: VCInterfaceStatus): ValidationIssue | null => {
	if (!status.config) {
		if (status.component?.startsWith('bthome') || status.vcType?.startsWith('bthome')) return null;
		return { key: 'vc_template_component_config_missing', message: 'Component config is missing' };
	}
	if (status.vcType === 'enum') return getEnumValidationIssue(status);
	if (status.vcType === 'number') return getNumberValidationIssue(status);
	return null;
};

/**
 * Validates an enum component's options, titles, default value, and current value.
 *
 * @param status - Enum component status to inspect.
 * @returns The first enum validation issue, or `null` when the enum is valid.
 */
const getEnumValidationIssue = (status: VCInterfaceStatus): ValidationIssue | null => {
	const options = status.config?.options;
	const titles = status.config?.meta?.ui?.titles;
	const value = status.rawValue ?? status.event;
	if (!options?.length || options.some((key) => typeof key !== 'string' || !key.trim())) {
		return { key: 'vc_template_enum_option_missing_key', message: 'Enum option is missing a key' };
	}
	const optionWithoutTitle = options.find(
		(key) => typeof titles?.[key] !== 'string' || !titles[key].trim(),
	);
	if (optionWithoutTitle) {
		return {
			key: 'vc_template_enum_option_missing_title',
			message: 'Enum option "{key}" is missing a title',
			values: { key: optionWithoutTitle },
		};
	}
	const duplicateKey = options.find((key, index) => options.indexOf(key) !== index);
	if (duplicateKey) {
		return {
			key: 'vc_template_enum_option_duplicate_key',
			message: 'Enum option key "{key}" is duplicated',
			values: { key: duplicateKey },
		};
	}
	const defaultValue = status.config?.default_value;
	if (defaultValue != null && !options.includes(defaultValue)) {
		return {
			key: 'vc_template_enum_default_value_invalid',
			message: 'Default value "{value}" is not present in options',
			values: { value: String(defaultValue) },
		};
	}
	if (value == null || !options.includes(value)) {
		return {
			key: 'vc_template_enum_current_value_invalid',
			message: 'Current value "{value}" is not present in options',
			values: { value: String(value) },
		};
	}
	return null;
};

/**
 * Validates a number component's bounds, step, default value, and current value.
 *
 * Missing bounds use the defaults from the Number component documentation. UI step metadata is
 * validated only when present because the documentation does not define a default or range for it.
 *
 * @param status - Number component status to inspect.
 * @returns The first number validation issue, or `null` when the number is valid.
 */
const getNumberValidationIssue = (status: VCInterfaceStatus): ValidationIssue | null => {
	const min = status.config.min ?? VIRTUAL_NUMBER_MIN_VALUE;
	const max = status.config.max ?? VIRTUAL_NUMBER_MAX_VALUE;
	const step = status.config.meta?.ui?.step;
	const normalizedMin = toFiniteNumber(min);
	const normalizedMax = toFiniteNumber(max);
	const normalizedStep = step == null ? null : toFiniteNumber(step);
	if (normalizedMin == null || normalizedMax == null || (step != null && normalizedStep == null)) {
		return {
			key: 'vc_template_number_config_invalid',
			message: 'Number min, max and step must be valid numbers',
		};
	}
	if (normalizedMin > normalizedMax) {
		return {
			key: 'vc_template_number_range_invalid',
			message: 'Number min must be less than or equal to max',
		};
	}
	if (normalizedStep != null && normalizedStep < 0) {
		return {
			key: 'vc_template_number_step_invalid',
			message: 'Number step must be greater than or equal to zero',
		};
	}
	const defaultValue = status.config.default_value;
	if (defaultValue != null) {
		const normalizedDefaultValue =
			typeof defaultValue === 'string' && defaultValue.trim() !== ''
				? Number(defaultValue)
				: defaultValue;
		if (!isNumberInRange(normalizedDefaultValue, normalizedMin, normalizedMax)) {
			return {
				key: 'vc_template_number_default_value_invalid',
				message: 'Number default value must be between min and max',
			};
		}
	}
	const value = status.rawValue ?? status.event;
	if (!isNumberInRange(value, normalizedMin, normalizedMax)) {
		return {
			key: 'vc_template_number_current_value_invalid',
			message: 'Current number value must be between min and max',
		};
	}
	return null;
};

/**
 * Converts a finite number or numeric string to a JavaScript number.
 *
 * @param value - Value to normalize.
 * @returns The normalized number, or `null` when the value is not numeric and finite.
 */
const toFiniteNumber = (value: unknown): number | null => {
	const normalized = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
	return isFiniteNumber(normalized) ? normalized : null;
};

/**
 * Checks whether a value is a finite JavaScript number.
 *
 * @param value - Value to inspect.
 * @returns Whether the value is a finite number.
 */
const isFiniteNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

/**
 * Checks whether a value is a finite number within an inclusive range.
 *
 * @param value - Value to inspect.
 * @param min - Inclusive lower bound.
 * @param max - Inclusive upper bound.
 * @returns Whether the value is between `min` and `max`, inclusive.
 */
const isNumberInRange = (value: unknown, min: number, max: number): boolean =>
	isFiniteNumber(value) && value >= min && value <= max;
